import { z } from 'zod';
// Campaign rollup feed-card payload — phase 1 of the rollup feature.
//
// Posted per-launched-creative_ads-campaign per-week. Mirrors the
// monday-recap shape: cache-row snapshot + Mag's authored text. The
// cache lives in campaign_rollup_cache; this card carries a frozen
// copy so the founder can still read it after the cache row gets
// refreshed with newer data.
const perDeliverableSchema = z.object({
    deliverableId: z.string().min(1).max(64),
    kind: z.string().min(1).max(32),
    label: z.string().min(1).max(200),
    spendUsd: z.number().min(0),
    conversions: z.number().min(0),
    cpaUsd: z.number().min(0),
    impressions: z.number().int().min(0),
    ctrPercent: z.number().min(0).max(100),
    lastMetricAt: z.string().nullable(),
});
const windowRollupSchema = z.object({
    windowDays: z.number().int().min(1).max(60),
    totalSpendUsd: z.number().min(0),
    totalConversions: z.number().min(0),
    metaSpendUsd: z.number().min(0),
    /** v1 = 0 always; meta_paid_ad_metrics_history doesn't carry a
     *  conversions column. Phase 2 will join from conversion_forwards. */
    metaConversions: z.number().min(0),
    googleSpendUsd: z.number().min(0),
    googleConversions: z.number().min(0),
    perDeliverable: z.array(perDeliverableSchema).max(20),
});
/** [54h.118] One executable move Mag recommends from the rollup. Rendered
 *  as a one-tap button on the card; tapping sends `chatPrompt` to Mag, who
 *  executes it through her authority-gated chat tools (adjust_campaign_budget
 *  / pause_campaign_deliverable) and confirms the trade-off. Only moves her
 *  tools can actually take (budget shifts/changes, pauses) — never rewrites
 *  or strategy (those stay in takeaways). */
export const campaignRollupRecommendedActionSchema = z.object({
    /** Button label — the move in mag voice, e.g. "shift préstamo's $2/day to alquiler". */
    label: z.string().min(1).max(80),
    /** The instruction sent to mag's chat on tap. Names the deliverable + the
     *  action + the why, phrased as what she'll do: "shift the préstamo search
     *  ad's daily budget into alquiler — préstamo is 84% budget-starved". */
    chatPrompt: z.string().min(1).max(280),
});
/** Mag's authored content for one campaign-rollup card. Length caps
 *  match the discipline established on takeover-read — the same
 *  three-iteration prompt-tightening methodology applies. */
export const magCampaignRollupAgentOutputSchema = z.object({
    /** 2-3 sentence headline summarizing the week, mag voice,
     *  lowercase. Opens with the verdict (mostly working / mixed /
     *  broken) and points at one or two specific numbers. */
    headline: z.string().min(1).max(600),
    /** One-line spend mix description ("60/40 meta vs google, all
     *  $560 spent at $9 blended cpa"). */
    spendMixSummary: z.string().min(1).max(280),
    /** 1-4 takeaways — specific observations grounded in numbers.
     *  Each bullet names a deliverable or platform. */
    takeaways: z.array(z.string().min(1).max(280)).min(1).max(4),
    /** 0-3 open questions Mag would ask the founder to clarify before
     *  acting on the read. Empty when the read is conclusive. */
    openQuestions: z.array(z.string().min(1).max(280)).max(3),
    /** Dashboard-row title. */
    shortTitle: z.string().min(1).max(80),
    /** [54h.118] 0-3 one-tap executable moves (budget shifts/changes, pauses)
     *  Mag recommends. Empty when there's no clear executable move. */
    recommendedActions: z.array(campaignRollupRecommendedActionSchema).max(3).default([]),
});
export const campaignRollupCardPayloadSchema = z.object({
    kind: z.literal('campaign_rollup'),
    /** Magig campaign id this rollup is for. */
    campaignId: z.string().uuid(),
    /** Campaign display name at the time of posting. */
    campaignName: z.string().min(1).max(200),
    /** Per-brand-per-week dedup key — Monday-in-brand-tz YYYY-MM-DD. */
    weekStartDate: z.string(),
    /** Brand tz. */
    tz: z.string().min(1).max(60),
    /** ISO timestamp the cache was computed at. Surfaces "data as of
     *  X" in the card UI. */
    computedAt: z.string().datetime(),
    /** Frozen snapshot of all three windows. */
    windows: z.array(windowRollupSchema).min(1).max(3),
    /** Mag's authored content. */
    headline: z.string().min(1).max(600),
    spendMixSummary: z.string().min(1).max(280),
    takeaways: z.array(z.string().min(1).max(280)).min(1).max(4),
    openQuestions: z.array(z.string().min(1).max(280)).max(3),
    shortTitle: z.string().min(1).max(80),
    /** v1 disclosure: Meta conversions aren't tracked in metrics
     *  history. Renderer shows this as a footnote. */
    v1Disclosures: z.array(z.string().min(1).max(280)).max(3),
    /** [54h.118] One-tap executable moves rendered as card buttons; tapping
     *  routes chatPrompt to mag, who executes via her authority-gated tools.
     *  Optional + default [] so pre-54h.118 cards still parse. */
    recommendedActions: z.array(campaignRollupRecommendedActionSchema).max(3).default([]),
    /** LLM cost of the rollup agent call. */
    costUsd: z.number().min(0),
});
//# sourceMappingURL=campaign-rollup-card.js.map